<template>
  <PlanCard>
    <PlanContent :show-discounted-price="shouldShowDiscountedUiVariant">
      <template #title>{{ title }}</template>
      <template #subtitle>
        <div class="w-full pt-2">
          <SelectInput
            id="select"
            name="volume-based-plan"
            :label="I18n.t('pricing.select_label')"
            :options="productOptions"
            :model-value="selectedOption"
            @update:modelValue="updateSelectedOption"
            controlled
          />
        </div>
      </template>

      <template #price>
        {{ selectedProduct.formattedPrice }}
      </template>

      <template #action>
        <Button
          full-width
          @click="() => buyProduct(selectedProduct)"
          :aria-label="I18n.t('pricing.buy_now')"
          :size="buttonSize"
        >
          {{ I18n.t("pricing.buy_now") }}
        </Button>
      </template>
      <template
        v-if="isYearlyBillingInterval && !Flipper.isEnabled('pricing_page_new_layout_experiment')"
        #billing-appendix
      ></template>

      <template v-if="!Flipper.isEnabled('pricing_page_new_layout_experiment')" #plan-description>
        <slot name="plan-description" />
      </template>
    </PlanContent>
  </PlanCard>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { BillingInterval, Product } from "./types";
import PlanCard from "./plan_card.vue";
import PlanContent from "./plan_content.vue";
import { Button, SelectInput, SelectOption } from "prism";
import { rbgPricingCardCtaClickedV100, rbgPricingCardVolumeSelectedV100 } from "kaleido-event-tracker";
import { PricingPlanType } from "./events";
import Flipper from "@/modules/flipper";
import { useMonitorSize } from "@/composables/monitor_size";

const I18n = inject("I18n");
const { isSmallTablet } = useMonitorSize();

const props = defineProps<{
  products: Product[];
  billingInterval?: BillingInterval;
  shouldShowDiscountedUiVariant?: boolean;
}>();
const parseProductToOption = (product: Product): SelectOption => ({
  label: `${product.credits.toLocaleString(I18n.locale)} ${
    product.credits > 1 ? I18n.t("pricing.credits") : I18n.t("pricing.credit")
  }`,
  secondaryLabel: `${product.formattedPrice}`,
  value: `${product.id}`,
});

const productOptions = computed(() => props.products.map(parseProductToOption));

const isExperimentEnabled = computed(() => Flipper.isEnabled("pricing_page_new_layout_experiment"));

const title = computed(() => (isExperimentEnabled.value ? I18n.t("pricing.pay_once") : I18n.t("pricing.payg.title")));

const buttonSize = computed(() => (!isSmallTablet.value && isExperimentEnabled.value ? "lg" : "default"));

const selectedOption = ref<SelectOption>(productOptions.value[0]);

const updateSelectedOption = (value: SelectOption) => {
  selectedOption.value = value;

  rbgPricingCardVolumeSelectedV100({
    type: PricingPlanType.Payg,
    credit_volume: selectedProduct.value.credits,
  });
};

const selectedProduct = computed(() => props.products.find((product) => product.id === selectedOption.value.value));
const isYearlyBillingInterval = computed(() => props.billingInterval === "yearly");

const buyProduct = (product: Product) => {
  rbgPricingCardCtaClickedV100({
    type: PricingPlanType.Payg,
    credit_volume: product.credits,
  });
  window.doPurchase(product.pProductId, "product");
};
</script>
